/** What a creature is, in words a player reads on its card.
 *
 * The rules never look here: every phrase describes an ability the kernel
 * already applies, from the ability's own fields, so a number on the card is
 * the number the battle uses. A kind with no words falls back to its key in
 * title case, and `creature-card.spec.ts` counts those as a defect - the
 * shipped content must have words for everything it ships.
 */

import type { ContentRegistry, CreatureAbility, CreatureDef, ResourceKind } from "@game/core";
import { RESOURCES } from "@game/core";
import { groupDigits, titleCase } from "./format.js";

const pct = (bp: number): string => `${String(Math.round(bp / 100))}%`;

const ACTIVATED_WORDS: Readonly<Record<string, string>> = Object.freeze({
  fortify: "Fortify: mends itself and the stacks beside it",
  warcry: "War cry: raises the whole line's attack for a few rounds",
  venom: "Venom: poisons every enemy standing beside it",
  terrify: "Terrify: lowers the morale of every enemy standing beside it",
});

/** One ability as a sentence, with its numbers. */
export function abilityPhrase(content: ContentRegistry, a: CreatureAbility): string {
  switch (a.kind) {
    case "ACCURATE_SHOT": return `Accurate shot: ${pct(a.chanceBp)} chance a shot kills one creature outright`;
    case "ACID_BREATH": return `Acid breath: every blow eats ${String(a.defenseDelta)} defence off the target for the battle`;
    case "ACTIVATED": {
      const words = ACTIVATED_WORDS[a.effect] ?? titleCase(a.effect);
      return `${words} (power ${String(a.power)}, every ${String(a.cooldownRounds)} rounds)`;
    }
    case "AGING": return "Aging: a struck stack's hit points are halved";
    case "ATTACK_ALL_AROUND": return "Strikes every enemy standing beside it";
    case "ATTACK_IGNORE": return `Shots ignore ${pct(a.pctBp)} of the target's defence`;
    case "BINDING": return "Binding: a struck stack cannot move while this one stands beside it";
    case "BLIND_ON_HIT": return `${pct(a.chanceBp)} chance a blow blinds the target`;
    case "CAST_AFTER_SHOT": {
      const names = a.spells.map((s) => content.spells.get(s)?.name ?? titleCase(s as string));
      return `After a shot, lays ${names.join(" or ")} on the target`;
    }
    case "CASTER": {
      // A Windcaller knows ten spells and casts three times; printing "casts Haste
      // 3 times" for the first of them would be a lie about both numbers.
      const names = a.spells.map((x) => content.spells.get(x)?.name ?? titleCase(x as string));
      const what = names.length === 1 ? names[0]! : `${String(names.length)} spells (${names.join(", ")})`;
      const cool = a.cooldownRounds === undefined || a.cooldownRounds <= 0
        ? ""
        : `, and not again for ${String(a.cooldownRounds)} rounds after each`;
      return `Casts ${what} ${String(a.casts)} ${a.casts === 1 ? "time" : "times"} a battle, at power ${String(a.power)}${cool}`;
    }
    case "MAGIC_ATTACK": {
      const cool = a.cooldownRounds === undefined || a.cooldownRounds <= 0
        ? ""
        : `, and not again for ${String(a.cooldownRounds)} rounds`;
      const school = a.school === "all" ? "raw magic" : `${a.school} magic`;
      return `May charge a blow with ${school} for +${String(a.bonusPct)}% damage, `
        + `${String(a.uses)} ${a.uses === 1 ? "time" : "times"} a battle${cool}`
        + " - magic resistance blunts it";
    }
    case "CHARM": return `${pct(a.chanceBp)} chance a blow charms the target for ${String(a.rounds)} rounds`;
    case "CONFUSION": return `${pct(a.chanceBp)} chance a blow drives the target blood_rage for ${String(a.rounds)} rounds`;
    case "CURSE_ON_HIT": return "Every blow curses the target";
    case "DEATH_CLOUD": return "Death cloud: shots also hurt everything beside the target";
    case "DEATH_STARE": return `Death stare: ${pct(a.chanceBp)} chance a blow kills one creature outright`;
    case "DEFENSE_PIERCE": return `Blows ignore ${pct(a.pctBp)} of the target's defence`;
    case "DETONATION": return "Detonates when it dies, burning everything beside it";
    case "DEVOUR_CORPSES": return "Devours a corpse to bank an extra strike";
    case "DISEASE": return `Disease: a struck stack loses ${String(-a.attackDelta)} attack and ${String(-a.defenseDelta)} defence`;
    case "DISPEL_ON_HIT": return "Every blow strips the target's spells";
    case "DOUBLE_ATTACK": return "Strikes twice";
    case "EXTRA_RETALIATIONS": return `Retaliates ${String(a.count + 1)} times a round`;
    case "FEAR": return `${pct(a.chanceBp)} chance a blow leaves the target too afraid to attack`;
    case "FEARLESS": return "Fearless: never loses a turn to bad morale";
    case "FEROCITY": return "Ferocity: a blow that kills earns another";
    case "FIRE_SHIELD": return `Fire shield: attackers take ${String(a.pct)}% of their own blow back`;
    case "FLYING": return "Flies";
    case "FREEZING_SHOT": return `${pct(a.chanceBp)} chance a shot freezes the target for ${String(a.rounds)} rounds`;
    case "HATES": {
      const name = content.creatures.get(a.creature)?.namePlural ?? titleCase(a.creature as string);
      return `Hates ${name}: +${String(a.bonusPct)}% damage against them`;
    }
    case "HEAT_STROKE": return "Heat stroke: vents on everything in front of it, friend and foe";
    case "JOUSTING": return `Jousting: +${String(a.pctPerHex)}% damage for every hex charged`;
    case "LIFE_DRAIN": return "Life drain: heals by the damage it deals";
    case "LUCK_AURA": return `+${String(a.delta)} luck to the whole army`;
    case "MAGIC_IMMUNITY": return a.maxSpellLevel >= 5
      ? "Immune to every spell"
      : `Immune to spells of level ${String(a.maxSpellLevel)} and below`;
    case "MAGIC_RESISTANCE": return `${String(a.pct)}% magic resistance`;
    case "MEDITATION": return "Meditation: once a battle, sits out a turn untouchable";
    case "MORALE_AURA": return `+${String(a.delta)} morale to the whole army`;
    case "NO_MELEE_PENALTY": return "No penalty in melee";
    case "NON_LIVING": return "Non-living: no morale, and mended by engineers";
    case "NO_RETALIATION": return "Its blows draw no retaliation";
    case "PACK_BOND": return `Pack bond: +${String(a.attack)} attack for every other pack beside it`;
    case "PARALYZE": return `${pct(a.chanceBp)} chance a blow paralyses the target`;
    case "PETRIFY": return `${pct(a.chanceBp)} chance a blow turns the target to stone`;
    case "POISON": return `Poison: a struck stack loses up to ${String(a.maxPct)}% of its hit points`;
    case "PREEMPTIVE_SHOT": return a.perRound === 0
      ? "Shoots first at whoever takes aim at it"
      : `Shoots first at whoever takes aim at it, ${String(a.perRound)} a round`;
    case "RANGED_SPREAD": return `Shots also hurt everything within ${String(a.radiusHexes)} of the target`;
    case "REDUCE_ENEMY_LUCK": return `-${String(a.delta)} luck to the enemy army`;
    case "REDUCE_ENEMY_MORALE": return `-${String(a.delta)} morale to the enemy army`;
    case "REGENERATE": return "Regenerates at the start of its turn";
    case "REPAIR": return `Repair: mends constructs and machines beside it, ${String(a.hpPerUnit)} a head`;
    case "RESURRECT_SELF": return "Rises again once when it falls";
    case "REVENGE": return "Revenge: hits harder the more of it has fallen";
    case "RUNE_MASTER": return "Rune master: earns runes faster";
    case "SCHOOL": return "School: fights better beside two more of its kind in water";
    case "SELF_LUCK": return `+${String(a.delta)} luck for itself`;
    case "SHOOTER": return `Shoots, ${String(a.shots)} shots`;
    case "SIEGE_MACHINE": return "Siege machine";
    case "WALL_BREAKER": return "May batter a town's walls as well as its troops";
    case "SOUL_HARVEST": return `Soul harvest: +${String(a.perKill)} attack per kill, up to +${String(a.cap)}`;
    case "SPELL_IMMUNITY": {
      const names = a.spells.map((s) => content.spells.get(s)?.name ?? titleCase(s as string));
      return `Immune to ${names.join(", ")}`;
    }
    case "SPELL_VULNERABILITY": {
      const names = a.spells.map((s) => content.spells.get(s)?.name ?? titleCase(s as string));
      return `Takes ${String(Math.round(a.mulFp / 10) - 100)}% more from ${names.join(", ")}`;
    }
    case "STRIKE_AND_RETURN": return "Strikes and returns to where it stood";
    case "SUMMONER": {
      const name = content.creatures.get(a.creature)?.namePlural ?? titleCase(a.creature as string);
      return `Summoner: once a battle, raises ${name} from a fallen ally`;
    }
    case "TELEPORT": return "Teleports";
    case "THREE_HEX_BREATH": return "Breath: hits the hex behind the target too";
    case "TWO_HEX": return "Two hexes wide";
    case "UNDEAD": return "Undead: no morale, immune to mind spells";
    case "UNLIMITED_RETALIATION": return "Retaliates against every blow";
    default: return titleCase((a as { kind: string }).kind);
  }
}

/** Every ability, in the order the definition lists them. */
export function abilityPhrases(content: ContentRegistry, def: CreatureDef): readonly string[] {
  return def.abilities.map((a) => abilityPhrase(content, a));
}

/** True when the phrase is words and not the fallback key. */
export function abilityHasWords(content: ContentRegistry, a: CreatureAbility): boolean {
  return abilityPhrase(content, a) !== titleCase(a.kind);
}

export interface StatLine {
  readonly label: string;
  readonly value: string;
}

/** The six numbers a classic creature card shows, plus growth and price. */
export function creatureStatLines(def: CreatureDef): readonly StatLine[] {
  const cost = RESOURCES
    .filter((r: ResourceKind) => (def.cost[r] ?? 0) > 0)
    .map((r: ResourceKind) => `${groupDigits(def.cost[r] ?? 0)} ${r}`)
    .join(", ");
  return [
    { label: "Attack", value: String(def.attack) },
    { label: "Defense", value: String(def.defense) },
    { label: "Damage", value: def.damageMin === def.damageMax ? String(def.damageMin) : `${String(def.damageMin)}-${String(def.damageMax)}` },
    { label: "Hit points", value: String(def.hp) },
    { label: "Speed", value: String(def.speed) },
    { label: "Growth", value: `${String(def.growth)} a week` },
    { label: "Cost", value: cost === "" ? "-" : cost },
  ];
}
